/**
 * 结算画面 —— 显示和牌的役种/符数/点数明细
 */

import { useMemo } from 'react';
import type { GameState } from '@mahjong/engine/src/game/state.js';
import {
  computeWinDetail,
  computeScoreChanges,
  formatYakuList,
  type WinDetail,
} from '@mahjong/engine/src/game/detail.js';
import { settleDraw } from '@mahjong/engine/src/game/flow.js';
import { tileIndex } from '@mahjong/engine/src/tile.js';
import { Tile, MeldDisplay } from './Tile.js';

const SEAT_NAMES = ['自家', '下家', '对家', '上家'] as const;

interface ResultOverlayProps {
  state: GameState;
  onNewGame: () => void;
  onContinue?: () => void;
}

export function ResultOverlay({ state, onNewGame, onContinue }: ResultOverlayProps) {
  const result = state.result;
  if (!result) return null;

  // 计算明细（和牌时）
  const detail: WinDetail | null = useMemo(() => {
    if (result.kind === 'draw') return null;
    try {
      return computeWinDetail(state, result.winner, result.kind, result.kind === 'ron' ? result.loser : null, result.winTile);
    } catch {
      return null;
    }
  }, [state, result]);

  const changes = result.scoreChanges;

  return (
    <div className="overlay">
      <div className="result-card">
        {/* 标题 */}
        <h2 className="result-title">
          {result.kind === 'tsumo' && '🎉 自摸'}
          {result.kind === 'ron' && '🎉 荣和'}
          {result.kind === 'draw' && '🀄 流局'}
        </h2>

        {/* 基本信息 */}
        {result.kind !== 'draw' && (
          <div className="result-winner">
            <span className="winner-seat">{SEAT_NAMES[result.winner]}</span>
            {result.kind === 'ron' && (
              <span className="loser-seat">放铳：{SEAT_NAMES[result.loser]}</span>
            )}
            <span className="win-tile-label">和了牌</span>
            <Tile index={tileIndex(result.winTile)} scale="sm" />
          </div>
        )}

        {/* 和牌明细（役种/符数/点数）*/}
        {detail && (
          <div className="win-detail">
            {detail.limitName && (
              <div className="detail-limit">{detail.limitName}</div>
            )}

            <div className="detail-row">
              <span className="detail-label">役种</span>
              <span className="detail-value detail-yaku">{formatYakuList(detail)}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">番数</span>
              <span className="detail-value">
                {detail.yakuman ? (
                  <strong>
                    役满{detail.yakumanCount > 1 ? `×${detail.yakumanCount}` : ''}
                  </strong>
                ) : (
                  <>
                    <strong>{detail.totalHan}</strong> 番
                    {detail.doraHan > 0 && (
                      <span className="detail-sub">（含宝牌 {detail.doraHan}）</span>
                    )}
                  </>
                )}
              </span>
            </div>

            {/* 役满不计符数 → 隐藏符数行 */}
            {!detail.yakuman && (
              <div className="detail-row">
                <span className="detail-label">符数</span>
                <span className="detail-value">
                  <strong>{detail.fu}</strong> 符
                  {detail.fuDetail && <span className="detail-sub">{detail.fuDetail}</span>}
                </span>
              </div>
            )}

            {/* 和牌手牌（渲染真实牌图，而不是文字）*/}
            <div className="detail-hand">
              <div className="detail-hand-label">和牌手牌</div>
              <div className="detail-hand-tiles">
                {detail.handTiles.map((idx, i) => (
                  <Tile key={`h${i}`} index={idx} scale="sm" />
                ))}
                {/* 和了牌（高亮）*/}
                <Tile
                  index={detail.winTileIndex}
                  scale="sm"
                  highlighted
                  className="detail-win-tile"
                />
                {detail.meldGroups.length > 0 && (
                  <MeldDisplay melds={detail.meldGroups} scale="sm" />
                )}
              </div>
            </div>
          </div>
        )}

        {/* 流局信息 */}
        {result.kind === 'draw' && (
          <div className="win-detail">
            <div className="detail-row">
              <span className="detail-label">原因</span>
              <span className="detail-value">{drawReason(result.reason)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">听牌</span>
              <span className="detail-value">
                {result.tenpaiSeats.length > 0
                  ? result.tenpaiSeats.map((s) => SEAT_NAMES[s]).join('、')
                  : '无（全员未听）'}
              </span>
            </div>
          </div>
        )}

        {/* 点数变化 */}
        <div className="result-changes">
          {changes.map((c, i) => (
            <div
              key={i}
              className={`change-item ${c > 0 ? 'change-plus' : c < 0 ? 'change-minus' : ''}`}
            >
              <span className="change-seat">{SEAT_NAMES[i]}</span>
              <span className="change-value">
                {c > 0 ? `+${c.toLocaleString()}` : c < 0 ? c.toLocaleString() : '±0'}
              </span>
              <span className="change-total">
                {state.players[i].score.toLocaleString()}
              </span>
            </div>
          ))}
        </div>

        {/* 按钮 */}
        <div className="result-actions">
          <button className="btn btn-primary btn-large" onClick={onNewGame}>
            再来一局
          </button>
        </div>
      </div>
    </div>
  );
}

function drawReason(r: string): string {
  const names: Record<string, string> = {
    exhaustive: '荒牌流局（牌山摸完）',
    nine_terminals: '九种九牌',
    four_winds: '四风连打',
    four_riichi: '四家立直',
  };
  return names[r] ?? r;
}
